import type { Trip } from "../types";
import { partyOf } from "../lib/trip-party";

export const TRIPS_STORAGE_KEY = "explore-casablanca:trips:v1";
export const LIKED_STORAGE_KEY = "explore-casablanca:liked:v1";
export const ACTIVE_TRIP_STORAGE_KEY = "explore-casablanca:active-trip:v1";

/** The party a trip is booked for when it was saved before trips had one. */
export const DEFAULT_TRIP_PEOPLE = 2;
/** The most people the create sheet lets a trip be for. */
export const MAX_TRIP_PEOPLE = 20;

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The local calendar day of a timestamp, as YYYY-MM-DD; null when unparseable. */
function localDayOf(timestamp: string): string | null {
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return null;
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Bring a stored trip up to the current shape.
 *
 * Trips are kept in the browser, so a trip saved last month has whatever
 * fields existed last month. Two arrived since: the date of day 1 and the
 * party size. A trip without a start date gets the day it was created, which
 * is exactly the date its days already showed, so nothing on screen moves. A
 * trip without a party is for two. Anything already set is left alone.
 */
export function migrateTrip(trip: Trip): Trip {
  const hasStart = typeof trip.startDate === "string" && ISO_DAY.test(trip.startDate);
  const people =
    typeof trip.people === "number" && Number.isFinite(trip.people) && trip.people >= 1
      ? Math.min(MAX_TRIP_PEOPLE, Math.round(trip.people))
      : DEFAULT_TRIP_PEOPLE;
  // Children come out of `people`; ages that are not a child's age, or more
  // children than leave one adult, are dropped rather than trusted.
  const childAges = partyOf({ people, childAges: trip.childAges }).childAges;
  const agesSame =
    (trip.childAges === undefined && childAges.length === 0) ||
    (trip.childAges !== undefined &&
      trip.childAges.length === childAges.length &&
      trip.childAges.every((a, i) => a === childAges[i]));
  if (hasStart && people === trip.people && agesSame) return trip;
  const startDate = hasStart ? trip.startDate : localDayOf(trip.createdAt) ?? undefined;
  const next: Trip = { ...trip, startDate, people };
  if (childAges.length > 0) return { ...next, childAges };
  const { childAges: _dropped, ...rest } = next;
  return rest;
}

function getStorage(): Storage | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage;
}

export function readTrips(): Trip[] {
  const storage = getStorage();
  if (!storage) return [];
  const raw = storage.getItem(TRIPS_STORAGE_KEY);
  if (raw === null) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as Trip[])
      .filter((trip) => trip && typeof trip === "object" && Array.isArray(trip.days))
      .map(migrateTrip);
  } catch {
    return [];
  }
}

/**
 * Fired on `window` after the stored trips change, with `{ origin }` as the
 * detail. Same-tab writes do not raise a `storage` event, so this is how the
 * planner hears that account sync (or the AI chat) rewrote the trips, and how
 * account sync hears that the planner did.
 */
export const TRIPS_CHANGED_EVENT = "mj-trips-changed";

export interface TripsChangedDetail {
  readonly origin?: string;
}

/**
 * Store the trips. A write that changes nothing is skipped (no event either),
 * which is what keeps the planner and account sync from echoing each other.
 */
export function writeTrips(trips: readonly Trip[], origin?: string): void {
  const storage = getStorage();
  if (!storage) return;
  const next = JSON.stringify(trips);
  if (storage.getItem(TRIPS_STORAGE_KEY) === next) return;
  storage.setItem(TRIPS_STORAGE_KEY, next);
  if (typeof window !== "undefined" && typeof CustomEvent !== "undefined") {
    window.dispatchEvent(
      new CustomEvent<TripsChangedDetail>(TRIPS_CHANGED_EVENT, { detail: { origin } }),
    );
  }
}

export function readLikedIds(): string[] {
  const storage = getStorage();
  if (!storage) return [];
  const raw = storage.getItem(LIKED_STORAGE_KEY);
  if (raw === null) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === "string");
  } catch {
    return [];
  }
}

export function writeLikedIds(ids: readonly string[]): void {
  const storage = getStorage();
  if (!storage) return;
  storage.setItem(LIKED_STORAGE_KEY, JSON.stringify(ids));
}

/** Forget this browser's own liked list, once its hearts live in the host's. */
export function clearLikedIds(): void {
  const storage = getStorage();
  if (!storage) return;
  storage.removeItem(LIKED_STORAGE_KEY);
}

export function readActiveTripId(): string | null {
  const storage = getStorage();
  if (!storage) return null;
  return storage.getItem(ACTIVE_TRIP_STORAGE_KEY);
}

export function writeActiveTripId(id: string | null): void {
  const storage = getStorage();
  if (!storage) return;
  if (id === null) storage.removeItem(ACTIVE_TRIP_STORAGE_KEY);
  else storage.setItem(ACTIVE_TRIP_STORAGE_KEY, id);
}
